import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";
import timezone from "dayjs/plugin/timezone";

dayjs.extend(utc);
dayjs.extend(timezone);

/**
 * CE Portal business hours, expressed in Eastern Time (America/New_York).
 * Quiet hours may not overlap this window — enforced server-side as well.
 * Keep in sync with server/helpers/linguistQuietHours.js.
 */
export const BUSINESS_HOURS = {
  start: "07:30",
  end: "18:00",
  days: [1, 2, 3, 4, 5],
  timezone: "America/New_York",
};

const TIME_PATTERN = /^(\d{1,2}):(\d{2})$/;

const parseTimeToMinutes = (timeValue) => {
  const match = TIME_PATTERN.exec(String(timeValue || "").trim());
  if (!match) {
    return null;
  }

  const hour = Number(match[1]);
  const minute = Number(match[2]);

  if (!Number.isInteger(hour) || !Number.isInteger(minute)) {
    return null;
  }
  if (hour < 0 || hour > 23 || minute < 0 || minute > 59) {
    return null;
  }

  return hour * 60 + minute;
};

/**
 * Convert a daily wall-clock time (minutes since midnight) in the preference
 * timezone to its Eastern Time minutes-of-day for today's reference date.
 * Returns null when the timezone cannot be resolved.
 */
const convertWallTimeToEtMinutes = (minutes, tz) => {
  try {
    const referenceDate = dayjs().format("YYYY-MM-DD");
    const hour = String(Math.floor(minutes / 60)).padStart(2, "0");
    const minute = String(minutes % 60).padStart(2, "0");

    const wallMoment = dayjs.tz(
      `${referenceDate} ${hour}:${minute}`,
      "YYYY-MM-DD HH:mm",
      tz,
    );

    if (!wallMoment.isValid()) {
      return null;
    }

    const etMoment = wallMoment.tz(BUSINESS_HOURS.timezone);
    return etMoment.hour() * 60 + etMoment.minute();
  } catch (error) {
    return null;
  }
};

/**
 * Validate a recurring daily quiet-hours window against business hours
 * (Mon–Fri 7:30 AM – 6:00 PM ET).
 *
 * Quiet hours are a half-open daily interval [start, end) that may wrap
 * midnight. The window is converted from the preference timezone to ET,
 * then checked for intersection with [07:30, 18:00). Touching edges
 * (e.g. 06:00–07:30 or 18:00–19:00) are allowed.
 *
 * @param {{ start: string, end: string, timezone: string }} params
 * @returns {{ valid: boolean, reason: string }}
 */
export const validateQuietHoursAgainstBusinessHours = ({
  start,
  end,
  timezone: preferenceTz,
} = {}) => {
  const startMinutes = parseTimeToMinutes(start);
  const endMinutes = parseTimeToMinutes(end);

  if (startMinutes === null || endMinutes === null) {
    return { valid: false, reason: "Quiet hours must use valid times (HH:MM)." };
  }

  if (startMinutes === endMinutes) {
    return { valid: false, reason: "Quiet hours start and end cannot be the same." };
  }

  const normalizedTz =
    typeof preferenceTz === "string" && preferenceTz.trim()
      ? preferenceTz.trim()
      : BUSINESS_HOURS.timezone;

  let startEtMinutes = startMinutes;
  let endEtMinutes = endMinutes;

  if (normalizedTz !== BUSINESS_HOURS.timezone) {
    startEtMinutes = convertWallTimeToEtMinutes(startMinutes, normalizedTz);
    endEtMinutes = convertWallTimeToEtMinutes(endMinutes, normalizedTz);

    if (startEtMinutes === null || endEtMinutes === null) {
      return {
        valid: false,
        reason: "Quiet hours timezone could not be converted to Eastern Time.",
      };
    }

    if (startEtMinutes === endEtMinutes) {
      return { valid: false, reason: "Quiet hours start and end cannot be the same." };
    }
  }

  const businessStartMinutes = parseTimeToMinutes(BUSINESS_HOURS.start);
  const businessEndMinutes = parseTimeToMinutes(BUSINESS_HOURS.end);

  // Half-open [start, end); when end <= start the interval wraps midnight.
  const wrapsMidnight = endEtMinutes <= startEtMinutes;
  const overlapsBusinessHours = wrapsMidnight
    ? startEtMinutes < businessEndMinutes || endEtMinutes > businessStartMinutes
    : startEtMinutes < businessEndMinutes && endEtMinutes > businessStartMinutes;

  if (!overlapsBusinessHours) {
    return { valid: true, reason: "" };
  }

  return {
    valid: false,
    reason:
      "Quiet hours cannot overlap business hours (Mon–Fri 7:30 AM – 6:00 PM ET).",
  };
};
